import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Service } from '@angular/core';
import { MadCloudResult } from '@common/http/models/mad-cloud-result.model';
import { PageFilter } from '@common/paged-data/types/page-filter.type';
import { Page } from '@common/paged-data/types/page.type';
import { User } from '@common/users/models/user.model';
import { ApiService } from '@portal-core/auth/services/api.service';
import { LicenseUserClientSideInfoKey } from '@portal-core/license-users/enums/license-user-client-side-info-key.enum';
import { LicenseUserSeatType } from '@portal-core/license-users/enums/license-user-seat-type.enum';
import { LicenseUserStatus } from '@portal-core/license-users/enums/license-user-status.enum';
import { LicenseBulkAddUserResponse } from '@portal-core/license-users/models/license-bulk-add-user-response.model';
import { LicenseUserClientSideInfo } from '@portal-core/license-users/models/license-user-client-side-info.model';
import { LicenseUser } from '@portal-core/license-users/models/license-user.model';
import { ProcessReport } from '@portal-core/processes/models/process-report.model';
import { Project } from '@portal-core/projects/models/project.model';
import { Team } from '@portal-core/teams/models/team.model';
import { BulkUploadUserRequest } from '@portal-core/users/models/bulk-upload-user-request.model';
import { SharePendingRequest } from '@portal-core/util/observables.decorators';
import { Observable } from 'rxjs';

@Service()
export class LicenseUsersApiService {
  private readonly http = inject(HttpClient);
  private readonly apiService = inject(ApiService);

  @SharePendingRequest()
  getCurrentLicenseUserByLicenseId$(licenseId: number): Observable<LicenseUser> {
    return this.http.get<LicenseUser>(`${this.apiService.centralApiBaseUri}/api/LicenseUsers/LicenseUser/${licenseId}`);
  }

  @SharePendingRequest()
  getLicenseUserById$(licenseUserId: number): Observable<LicenseUser> {
    return this.http.get<LicenseUser>(`${this.apiService.centralApiBaseUri}/api/LicenseUsers/${licenseUserId}`);
  }

  @SharePendingRequest()
  getLicenseUserByUserId$(userId: string, licenseId: number): Observable<LicenseUser> {
    return this.http.get<LicenseUser>(`${this.apiService.centralApiBaseUri}/api/LicenseUsers/License/${licenseId}/User/${userId}`);
  }

  @SharePendingRequest()
  getLicenseUsersById$(licenseUserIds: number[]): Observable<LicenseUser[]> {
    return this.http.post<LicenseUser[]>(`${this.apiService.centralApiBaseUri}/api/LicenseUsers/getBatchLicenseUsersByIds`, licenseUserIds);
  }

  @SharePendingRequest()
  getLicenseUsersPageByLicenseId$(licenseId: number, filter: PageFilter): Observable<Page<LicenseUser>> {
    return this.http.post<Page<LicenseUser>>(`${this.apiService.centralApiBaseUri}/api/LicenseUsers/${licenseId}/getLicenseUsersList`, filter);
  }

  @SharePendingRequest()
  getAllLicenseUsersByLicenseId$(licenseId: number, getViewers: boolean): Observable<LicenseUser[]> {
    return this.http.get<LicenseUser[]>(`${this.apiService.centralApiBaseUri}/api/licenses/${licenseId}/users?getViewers=${getViewers}`);
  }

  @SharePendingRequest()
  getLicenseUsersByUserIds$(licenseId: number, userIds: string[]) {
    return this.http.post<LicenseUser[]>(`${this.apiService.centralApiBaseUri}/api/LicenseUsers/License/${licenseId}/UserList`, userIds);
  }

  @SharePendingRequest()
  getLicenseUsersAdministrations$(licenseId: number): Observable<User[]> {
    return this.http.get<User[]>(`${this.apiService.centralApiBaseUri}/api/licenses/${licenseId}/userAdministrations`);
  }

  @SharePendingRequest()
  getBulkImportProcessReport$(processId: number): Observable<ProcessReport> {
    return this.http.get<ProcessReport>(`${this.apiService.centralApiBaseUri}/api/LicenseUsers/${processId}/BulkImportStatus`);
  }

  @SharePendingRequest()
  getTeams$(licenseUserId: number, filter: PageFilter): Observable<Page<Team>> {
    return this.http.post<Page<Team>>(`${this.apiService.centralApiBaseUri}/api/LicenseUsers/${licenseUserId}/Teams`, filter);
  }

  @SharePendingRequest()
  getProjects$(licenseUserId: number, filter: PageFilter): Observable<Page<Project>> {
    return this.http.post<Page<Project>>(`${this.apiService.centralApiBaseUri}/api/LicenseUsers/${licenseUserId}/Projects`, filter);
  }

  removeUserFromLicense$(licenseId: number, userId: string): Observable<void> {
    return this.http.delete<void>(`${this.apiService.centralApiBaseUri}/api/licenseusers/${licenseId}/user/${userId}`);
  }

  updateLicenseUser$(licenseId: number, userId: string, isActive: boolean): Observable<void> {
    // The PutUser endpoint reads isActive (and optional seatType) from the query string and takes no
    // request body, so send null rather than a model.
    return this.http.put<void>(`${this.apiService.centralApiBaseUri}/api/licenseusers/${licenseId}/User/${userId}?isActive=${isActive}`, null);
  }

  reinviteUser$(licenseId: number, userId: string): Observable<void> {
    return this.http.put<void>(`${this.apiService.centralApiBaseUri}/api/licenseusers/${licenseId}/User/${userId}/reinvite`, {});
  }

  addUsersToLicense$(licenseId: number, seatType: LicenseUserSeatType, bulkUploadUserRequest: BulkUploadUserRequest, siteId?: number): Observable<LicenseBulkAddUserResponse> {
    const siteIdParam = siteId ? `&siteId=${siteId}` : '';

    return this.http.post<LicenseBulkAddUserResponse>(
      `${this.apiService.centralApiBaseUri}/api/licenseusers/${licenseId}/addUsers?seatType=${seatType}${siteIdParam}`, bulkUploadUserRequest
    );
  }

  bulkDeleteLicenseUsers$(licenseId: number, licenseUserIds: number[]): Observable<void> {
    return this.http.post<void>(`${this.apiService.centralApiBaseUri}/api/LicenseUsers/${licenseId}/BulkDeleteLicenseUsers`, licenseUserIds);
  }

  bulkSetLicenseUsersSeatType$(licenseId: number, seatType: LicenseUserSeatType, licenseUserIds: number[]): Observable<void> {
    return this.http.put<void>(`${this.apiService.centralApiBaseUri}/api/LicenseUsers/${licenseId}/BulkSetLicenseUsersSeatType/${seatType}`, licenseUserIds);
  }

  bulkSetLicenseUsersStatus$(licenseId: number, status: LicenseUserStatus, licenseUserIds: number[]): Observable<void> {
    return this.http.put<void>(`${this.apiService.centralApiBaseUri}/api/LicenseUsers/${licenseId}/BulkSetLicenseUsersStatus/${status}`, licenseUserIds);
  }

  @SharePendingRequest()
  getClientSideSettings$(licenseId: number, key: LicenseUserClientSideInfoKey): Observable<MadCloudResult<LicenseUserClientSideInfo>> {
    return this.http.get<MadCloudResult<LicenseUserClientSideInfo>>(`${this.apiService.centralApiBaseUri}/api/licenseusers/${licenseId}/getClientSideSettings/${key}`);
  }

  saveClientSideSettings$(licenseId: number, key: LicenseUserClientSideInfoKey, settings: unknown): Observable<MadCloudResult<void>> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });
    const settingsAsStr = !settings ? settings : `=${JSON.stringify(settings)}`;
    return this.http.post<MadCloudResult<void>>(
      `${this.apiService.centralApiBaseUri}/api/licenseusers/${licenseId}/saveClientSideSettings/${key}`,
      settingsAsStr,
      { headers });
  }
}
